import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import EnterKeyNextField from "@/components/EnterKeyNextField";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import PublicLanding from "@/pages/PublicLanding";
import PublicAppointment from "@/pages/PublicAppointment";
import PublicAppointmentSuccess from "@/pages/PublicAppointmentSuccess";
import PublicSelfRegister from "@/pages/PublicSelfRegister";
import UnifiedLayout from "@/layouts/UnifiedLayout";
import Dashboard from "@/pages/super-admin/Dashboard";
import Hospitals from "@/pages/super-admin/Hospitals";
import Admins from "@/pages/super-admin/Admins";
import HospitalDashboard from "@/pages/hospital-admin/Dashboard";
import UserManagement from "@/pages/hospital-admin/UserManagement";
import WhatsAppCampaignsPage from "@/pages/hospital-admin/WhatsAppCampaignsPage";
import WhatsAppMediaLibraryPage from "@/pages/hospital-admin/WhatsAppMediaLibraryPage";
import ShiftManagement from "@/pages/hospital-admin/ShiftManagement";
import OpdFees from "@/pages/hospital-admin/OpdFees";
import GSTManagement from "@/pages/hospital-admin/GSTManagement";
import Patients from "@/pages/hospital-admin/Patients";
import PatientDetails from "@/pages/hospital-admin/PatientDetails";
import ProtectedRoute from "@/components/ProtectedRoute";
import { DOCTOR_PORTAL_ROLE_LIST } from "@/lib/clinicalRoles";
import ReceptionistDashboard from "@/pages/receptionist/Dashboard";
import SalesDashboard from "@/pages/sales/Dashboard";
import SalesManagerQueueManagementPage from "@/pages/sales/SalesManagerQueueManagementPage";
import { SearchProvider } from "@/contexts/SearchContext";
import DoctorDashboard from "./pages/doctor/DoctorDashboard";
import DoctorTeamDashboard from "./pages/doctor-team/DoctorTeamDashboard";
import Appointments from "./pages/doctor/Appointments";
import IPDManagement from "./pages/doctor/IPDManagement";
import IPDPatientDetail from "./pages/doctor/IPDPatientDetail";
import ConsultationPage from "./pages/consultation/ConsultationPage";
import DiseaseTemplate from "./pages/doctor/DiseaseTemplate";
import DiagnosisRecord from "./pages/consultation/DiagnosisRecord";
import LabDashboard from "./pages/lab/LabDashboard";
import AppointmentManagement from "./pages/appointment/AppointmentManagement";
import FollowUpsSection from "./pages/receptionist/FollowUpsSection";
import IpdDischargeFollowUpsPage from "./pages/receptionist/IpdDischargeFollowUpsPage";
import SurgicalAppointments from "./pages/receptionist/SurgicalAppointments";
import DepartmentManagement from "./pages/hospital-admin/DepartmentManagement";
import CompletedTests from "./pages/lab/CompletedTests";
import TestsFromReceptionist from "./pages/lab/TestsFromReceptionist";
import IPDQueuePage from "./pages/ipd/IPDQueuePage";
import IPDOfflineQueuePage from "./pages/ipd/IPDOfflineQueuePage";
import DayCareQueuePage from "./pages/daycare/DayCareQueuePage";
import DayCareCasePage from "./pages/daycare/DayCareCasePage";
import DayCareBillingPage from "./pages/daycare/DayCareBillingPage";
import DayCareSetupPage from "./pages/hospital-admin/DayCareSetupPage";
import TestFromDoctors from "./pages/lab/TestFromDoctors";
import IPDLabTestManager from "./pages/lab/IPDLabTestManager";
import CreateLabTest from "./pages/lab/CreateLabTest";
import CreateLabTestAppointment from "./pages/receptionist/CreateLabTestAppointment";
import PendingLabBills from "./pages/receptionist/PendingLabBills";
import PatientBillsPage from "./pages/receptionist/PatientBillsPage";
import IPDAdminDashboard from "./pages/admin/IPDAdminDashboard";
import IPDBillingPage from "./pages/ipd/IPDBillingPage";
import IPDPendingCollectionPage from "./pages/ipd/IPDPendingCollectionPage";
import WardManagement from "./pages/admin/WardManagement";
import InsuranceManagement from "./pages/admin/InsuranceManagement";
import IntelligenceCommandCentrePage from "./pages/hospital-admin/IntelligenceCommandCentrePage";
import DoctorIntelligencePage from "./pages/hospital-admin/intelligence/DoctorIntelligencePage";
import DepartmentIntelligencePage from "./pages/hospital-admin/intelligence/DepartmentIntelligencePage";
import OperationsIntelligencePage from "./pages/hospital-admin/intelligence/OperationsIntelligencePage";
import PharmacyIntelligencePage from "./pages/hospital-admin/intelligence/PharmacyIntelligencePage";
import LabIntelligencePage from "./pages/hospital-admin/intelligence/LabIntelligencePage";
import FinanceIntelligencePage from "./pages/hospital-admin/intelligence/FinanceIntelligencePage";
import ManagementAttentionPage from "./pages/hospital-admin/intelligence/ManagementAttentionPage";
import LeakageIntelligencePage from "./pages/hospital-admin/intelligence/LeakageIntelligencePage";
import InsightsIntelligencePage from "./pages/hospital-admin/intelligence/InsightsIntelligencePage";
import BenchmarkIntelligencePage from "./pages/hospital-admin/intelligence/BenchmarkIntelligencePage";
import IntelligenceSettingsPage from "./pages/hospital-admin/intelligence/IntelligenceSettingsPage";
import IntelligenceDataQualityPage from "./pages/hospital-admin/intelligence/IntelligenceDataQualityPage";
import BusinessReports from "./pages/hospital-admin/BusinessReports";
import LoyaltyProgramPage from "./pages/hospital-admin/LoyaltyProgramPage";
import AppAnalyticsPage from "./pages/hospital-admin/AppAnalyticsPage";
import WebsiteAnalyticsPage from "./pages/hospital-admin/WebsiteAnalyticsPage";
import TrueCareWelcomeAnalyticsPage from "./pages/hospital-admin/TrueCareWelcomeAnalyticsPage";
import PatientPortalLogin from "./pages/patient-portal/PatientPortalLogin";
import PatientPortalShell from "./pages/patient-portal/PatientPortalShell";
import PatientPortalForgotPassword from "./pages/patient-portal/PatientPortalForgotPassword";
import PatientPortalResetPassword from "./pages/patient-portal/PatientPortalResetPassword";
import PatientPortalLayout from "./pages/patient-portal/PatientPortalLayout";
import RequirePatientToken from "./pages/patient-portal/RequirePatientToken";
import PatientPortalHome from "./pages/patient-portal/PatientPortalHome";
import PatientPortalLoyalty from "./pages/patient-portal/PatientPortalLoyalty";
import PatientPortalRecords from "./pages/patient-portal/PatientPortalRecords";
import PatientPortalChallenges from "./pages/patient-portal/PatientPortalChallenges";
import PatientPortalNotifications from "./pages/patient-portal/PatientPortalNotifications";
import PatientPortalWallet from "./pages/patient-portal/PatientPortalWallet";
import PatientPortalBooking from "./pages/patient-portal/PatientPortalBooking";
import PatientPortalFamily from "./pages/patient-portal/PatientPortalFamily";
import PatientPortalReferrals from "./pages/patient-portal/PatientPortalReferrals";
import PatientPortalContact from "./pages/patient-portal/PatientPortalContact";
import PatientPortalDeleteAccount from "./pages/patient-portal/PatientPortalDeleteAccount";
import OperationalReportsPage from "./pages/hospital-admin/OperationalReportsPage";
import CustomerVoicePage from "./pages/hospital-admin/CustomerVoicePage";
import PendingPaymentsReportPage from "./pages/hospital-admin/PendingPaymentsReportPage";
import PatientDueBalancesPage from "./pages/hospital-admin/PatientDueBalancesPage";
import PatientWiseBillsReportPage from "./pages/hospital-admin/PatientWiseBillsReportPage";
import ReconciliationPage from "./pages/hospital-admin/ReconciliationPage";
import AuditLogPage from "./pages/hospital-admin/AuditLogPage";
import SelfRegisterQRPage from "./pages/hospital-admin/SelfRegisterQRPage";
import MiniCrmSettings from "./pages/hospital-admin/MiniCrmSettings";
import SarvamIntegrationsPage from "./pages/hospital-admin/SarvamIntegrationsPage";
import SocialInboxPage from "./pages/hospital-admin/SocialInboxPage";
import SocialSettingsPage from "./pages/hospital-admin/SocialSettingsPage";
import SocialAnalyticsPage from "./pages/hospital-admin/SocialAnalyticsPage";
import MetaConversionLogsPage from "./pages/hospital-admin/MetaConversionLogsPage";
import MetaDashboardPage from "./pages/hospital-admin/MetaDashboardPage";
import CampaignAttributionDashboardPage from "./pages/hospital-admin/CampaignAttributionDashboardPage";
import MetaIntegrationPage from "./pages/super-admin/MetaIntegrationPage";
import GoogleConversionLogsPage from "./pages/hospital-admin/GoogleConversionLogsPage";
import GoogleIntegrationPage from "./pages/super-admin/GoogleIntegrationPage";
import TenantGroupsPage from "./pages/super-admin/TenantGroupsPage";
import ChainDashboardPage from "./pages/hospital-admin/chain/ChainDashboardPage";
import ChainHospitalsPage from "./pages/hospital-admin/chain/ChainHospitalsPage";
import ChainAdminsPage from "./pages/hospital-admin/chain/ChainAdminsPage";
import PartnerAnalyticsPage from "./pages/hospital-admin/partners/PartnerAnalyticsPage";
import PartnerApprovalsPage from "./pages/hospital-admin/partners/PartnerApprovalsPage";
import PartnerLeadsPage from "./pages/hospital-admin/partners/PartnerLeadsPage";
import PartnerManagementPage from "./pages/hospital-admin/partners/PartnerManagementPage";
import ExpensesPage from "./pages/hospital-admin/ExpensesPage";
import AdditionalRevenuePage from "./pages/hospital-admin/AdditionalRevenuePage";
import ReceivablesPage from "./pages/hospital-admin/ReceivablesPage";
import VendorManagementPage from "./pages/hospital-admin/VendorManagementPage";
import AccountingLayout from "./pages/accounting/AccountingLayout";
import AccountingDashboardPage from "./pages/accounting/AccountingDashboardPage";
import AccountingVouchersPage from "./pages/accounting/AccountingVouchersPage";
import AccountingVoucherFormPage from "./pages/accounting/AccountingVoucherFormPage";
import AccountingVoucherDetailPage from "./pages/accounting/AccountingVoucherDetailPage";
import AccountingTrialBalancePage from "./pages/accounting/AccountingTrialBalancePage";
import AccountingGeneralLedgerPage from "./pages/accounting/AccountingGeneralLedgerPage";
import AccountingChartOfAccountsPage from "./pages/accounting/AccountingChartOfAccountsPage";
import AccountingPeriodsPage from "./pages/accounting/AccountingPeriodsPage";
import AccountingOpeningBalancesPage from "./pages/accounting/AccountingOpeningBalancesPage";
import AccountingOpsReconciliationPage from "./pages/accounting/AccountingOpsReconciliationPage";
import AccountingAgeingPage from "./pages/accounting/AccountingAgeingPage";
import AccountingTaxPage from "./pages/accounting/AccountingTaxPage";
import AccountingBankPharmacyPage from "./pages/accounting/AccountingBankPharmacyPage";
import AccountingStatementsPage from "./pages/accounting/AccountingStatementsPage";
import AccountingCfoPage from "./pages/accounting/AccountingCfoPage";
import VendorDashboardPage from "./pages/vendor/VendorDashboardPage";
import IPDBillEditRequestsPage from "./pages/hospital-admin/IPDBillEditRequestsPage";
import IpdExcessCollectionPage from "./pages/hospital-admin/IpdExcessCollectionPage";
import NurseDashboard from "./pages/nurse/NurseDashboard";
import NurseIPDManagement from "./pages/nurse/NurseIPDManagement";
import PharmacistDashboard from "./pages/pharmacist/PharmacistDashboard";
import PharmacyQueuePage from "./pages/pharmacist/PharmacyQueuePage";
import PharmacistIPDPharmaPage from "./pages/pharmacist/PharmacistIPDPharmaPage";
import PharmacyStockPage from "./pages/pharmacist/PharmacyStockPage";
import PharmacyReturnsPage from "./pages/pharmacist/PharmacyReturnsPage";
import PharmacyReturnHistoryPage from "./pages/pharmacist/PharmacyReturnHistoryPage";
import PharmacyWalkInSalePage from "./pages/pharmacist/PharmacyWalkInSalePage";
import PharmacyStockInPage from "./pages/pharmacist/PharmacyStockInPage";
import PharmacyPurchaseOrdersPage from "./pages/pharmacist/PharmacyPurchaseOrdersPage";
import PharmaVendorPortalPage from "./pages/pharma-vendor/PharmaVendorPortalPage";
import LeadsPage from "./pages/sales/LeadsPage";
import PharmacySalesReportPage from "./pages/pharmacist/PharmacySalesReportPage";
import VendorsPage from "./pages/pharmacist/VendorsPage";
import VendorDetailPage from "./pages/pharmacist/VendorDetailPage";
import OTPharmacyDashboardPage from "./pages/pharmacist/OTPharmacyDashboardPage";
import OTTransferPage from "./pages/pharmacist/OTTransferPage";
import OTConsumptionEntryPage from "./pages/pharmacist/OTConsumptionEntryPage";
import OTGeneralConsumptionPage from "./pages/pharmacist/OTGeneralConsumptionPage";
import OTReconciliationPage from "./pages/pharmacist/OTReconciliationPage";
import OTReportsPage from "./pages/pharmacist/OTReportsPage";
import LeadInsightsPage from "./pages/common/LeadInsightsPage";
import LeadAssignmentAuditPage from "./pages/common/LeadAssignmentAuditPage";
import SarvamAgentOutcomesPage from "./pages/sales/SarvamAgentOutcomesPage";
import LabReportsPage from "./pages/lab/LabReportsPage";
import LabExternalVendorsPage from "./pages/hospital-admin/LabExternalVendorsPage";
import LabPayoutReportPage from "./pages/hospital-admin/LabPayoutReportPage";
import PharmacyVendorsPage from "./pages/pharmacy/PharmacyVendorsPage";
import PharmacyVendorDetailPage from "./pages/pharmacy/PharmacyVendorDetailPage";
import CrmIpdCasesPage from "./pages/common/CrmIpdCasesPage";
import CrmIpdAdmittedPage from "./pages/common/CrmIpdAdmittedPage";
import PartnerLogin from "./pages/partner/PartnerLogin";
import PartnerForgotPassword from "./pages/partner/PartnerForgotPassword";
import PartnerResetPassword from "./pages/partner/PartnerResetPassword";
import PartnerRegister from "./pages/partner/PartnerRegister";
import PartnerPortalLayout from "./pages/partner/PartnerPortalLayout";
import RequirePartnerToken from "./pages/partner/RequirePartnerToken";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerReferrals from "./pages/partner/PartnerReferrals";
import PartnerPoints from "./pages/partner/PartnerPoints";
import PartnerPayouts from "./pages/partner/PartnerPayouts";
import PartnerProfile from "./pages/partner/PartnerProfile";
import ReferralPartnerApprovalsPage from "./pages/super-admin/ReferralPartnerApprovalsPage";
import CharteredAccountantLayout from "./pages/chartered-accountant/CharteredAccountantLayout";
import CaDashboard from "./pages/chartered-accountant/CaDashboard";
import CaRevenueReports from "./pages/chartered-accountant/CaRevenueReports";
import CaExpenseReports from "./pages/chartered-accountant/CaExpenseReports";
import CaTdsReports from "./pages/chartered-accountant/CaTdsReports";
import CaGstReports from "./pages/chartered-accountant/CaGstReports";
import CaBillWiseReports from "./pages/chartered-accountant/CaBillWiseReports";
import InvestorLayout from "./pages/investor/InvestorLayout";

function PatientDetailsWrapper() {
	const { patientId } = useParams<{ patientId: string }>();
	return <PatientDetails patientId={patientId!} />;
}

export default function App() {
	return (
		<AuthProvider>
			<SearchProvider>
				<EnterKeyNextField />
				<Toaster />
				<Routes>
					{/* Public Routes */}
					<Route
						path="/"
						element={<PublicLanding />}
					/>
					<Route
						path="/book-appointment"
						element={<PublicAppointment />}
					/>
					<Route
						path="/appointment-success"
						element={<PublicAppointmentSuccess />}
					/>
					<Route
						path="/self-register"
						element={<PublicSelfRegister />}
					/>
					{/* Referral partner portal */}
					<Route path="/partner">
						<Route path="login" element={<PartnerLogin />} />
						<Route path="forgot-password" element={<PartnerForgotPassword />} />
						<Route path="reset-password" element={<PartnerResetPassword />} />
						<Route path="register" element={<PartnerRegister />} />
						<Route element={<RequirePartnerToken />}>
							<Route element={<PartnerPortalLayout />}>
								<Route index element={<PartnerDashboard />} />
								<Route path="referrals" element={<PartnerReferrals />} />
								<Route path="points" element={<PartnerPoints />} />
								<Route path="payouts" element={<PartnerPayouts />} />
								<Route path="profile" element={<PartnerProfile />} />
							</Route>
						</Route>
					</Route>

					<Route path="/patient-loyalty" element={<PatientPortalShell />}>
						<Route path="login" element={<PatientPortalLogin />} />
						<Route path="forgot-password" element={<PatientPortalForgotPassword />} />
						<Route path="reset-password" element={<PatientPortalResetPassword />} />
						<Route path="delete-account" element={<PatientPortalDeleteAccount />} />
						<Route element={<RequirePatientToken />}>
							<Route element={<PatientPortalLayout />}>
								<Route index element={<PatientPortalHome />} />
								<Route path="loyalty" element={<PatientPortalLoyalty />} />
								<Route path="records" element={<PatientPortalRecords />} />
								<Route path="challenges" element={<PatientPortalChallenges />} />
								<Route path="notifications" element={<PatientPortalNotifications />} />
								<Route path="wallet" element={<PatientPortalWallet />} />
								<Route path="booking" element={<PatientPortalBooking />} />
								<Route path="family" element={<PatientPortalFamily />} />
								<Route path="referrals" element={<PatientPortalReferrals />} />
								<Route path="contact" element={<PatientPortalContact />} />
							</Route>
						</Route>
					</Route>
					<Route
						path="/login"
						element={<Login />}
					/>
					<Route
						path="/reset-password"
						element={<ResetPassword />}
					/>

					{/* Chartered accountant portal (external CA — financial reports only) */}
					<Route
						path="/chartered-accountant/*"
						element={
							<ProtectedRoute allowedRoles={["CHARTERED_ACCOUNTANT"]}>
								<CharteredAccountantLayout />
							</ProtectedRoute>
						}
					>
						<Route index element={<CaDashboard />} />
						<Route path="revenue" element={<CaRevenueReports />} />
						<Route path="bills" element={<CaBillWiseReports />} />
						<Route path="expenses" element={<CaExpenseReports />} />
						<Route path="receivables" element={<ReceivablesPage />} />
						<Route path="tds" element={<CaTdsReports />} />
						<Route path="gst" element={<CaGstReports />} />
						<Route path="accounting" element={<AccountingLayout />}>
							<Route index element={<AccountingDashboardPage />} />
							<Route path="vouchers" element={<AccountingVouchersPage />} />
							<Route path="vouchers/new" element={<AccountingVoucherFormPage />} />
							<Route path="vouchers/:voucherId" element={<AccountingVoucherDetailPage />} />
							<Route path="trial-balance" element={<AccountingTrialBalancePage />} />
							<Route path="general-ledger" element={<AccountingGeneralLedgerPage />} />
							<Route path="chart-of-accounts" element={<AccountingChartOfAccountsPage />} />
							<Route path="periods" element={<AccountingPeriodsPage />} />
							<Route path="opening-balances" element={<AccountingOpeningBalancesPage />} />
							<Route path="ops-reconciliation" element={<AccountingOpsReconciliationPage />} />
							<Route path="ageing" element={<AccountingAgeingPage />} />
							<Route path="tax" element={<AccountingTaxPage />} />
							<Route path="bank-pharmacy" element={<AccountingBankPharmacyPage />} />
							<Route path="statements" element={<AccountingStatementsPage />} />
							<Route path="cfo" element={<AccountingCfoPage />} />
						</Route>
					</Route>

					{/* Investor portal (external — assigned Business Reports only) */}
					<Route
						path="/investor/*"
						element={
							<ProtectedRoute allowedRoles={["INVESTOR"]}>
								<InvestorLayout />
							</ProtectedRoute>
						}
					>
						<Route index element={<Navigate to="/investor/business-reports" replace />} />
						<Route path="business-reports" element={<BusinessReports />} />
					</Route>

					{/* Vendor portal — raise invoices & track payment status */}
					<Route
						path="/vendor/*"
						element={
							<ProtectedRoute allowedRoles={["VENDOR"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route index element={<Navigate to="/vendor/dashboard" replace />} />
						<Route path="dashboard" element={<VendorDashboardPage />} />
						<Route path="invoices/:id" element={<VendorDashboardPage />} />
					</Route>

					{/* Pharmacy supplier portal — PO delivery & invoice upload */}
					<Route
						path="/pharma-vendor/*"
						element={
							<ProtectedRoute allowedRoles={["PHARMA_VENDOR"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route index element={<Navigate to="/pharma-vendor/orders" replace />} />
						<Route path="orders" element={<PharmaVendorPortalPage />} />
					</Route>

					{/* Super Admin Routes */}
					<Route
						path="/super-admin/*"
						element={
							<ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/super-admin/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<Dashboard />}
						/>
						<Route path="tenants" element={<TenantGroupsPage />} />
						<Route
							path="hospitals"
							element={<Hospitals />}
						/>
						<Route
							path="admins"
							element={<Admins />}
						/>
						<Route
							path="intelligence"
							element={<IntelligenceCommandCentrePage />}
						/>
						<Route
							path="intelligence/doctors"
							element={<DoctorIntelligencePage />}
						/>
						<Route
							path="intelligence/departments"
							element={<DepartmentIntelligencePage />}
						/>
						<Route
							path="intelligence/operations"
							element={<OperationsIntelligencePage />}
						/>
						<Route
							path="intelligence/pharmacy"
							element={<PharmacyIntelligencePage />}
						/>
						<Route
							path="intelligence/lab"
							element={<LabIntelligencePage />}
						/>
						<Route
							path="intelligence/finance"
							element={<FinanceIntelligencePage />}
						/>
						<Route
							path="intelligence/attention"
							element={<ManagementAttentionPage />}
						/>
						<Route
							path="intelligence/benchmarks"
							element={<BenchmarkIntelligencePage basePath="/super-admin" />}
						/>
						<Route
							path="intelligence/settings"
							element={<IntelligenceSettingsPage basePath="/super-admin" />}
						/>
						<Route
							path="intelligence/data-quality"
							element={<IntelligenceDataQualityPage basePath="/super-admin" />}
						/>
						<Route
							path="intelligence/leakage"
							element={<LeakageIntelligencePage />}
						/>
						<Route
							path="intelligence/insights"
							element={<InsightsIntelligencePage />}
						/>
						<Route
							path="business-reports"
							element={<BusinessReports />}
						/>
						<Route
							path="pending-payments"
							element={<PendingPaymentsReportPage />}
						/>
						<Route
							path="patient-due-balances"
							element={<PatientDueBalancesPage />}
						/>
						<Route
							path="operational-reports"
							element={<OperationalReportsPage />}
						/>
						<Route
							path="customer-voice"
							element={<CustomerVoicePage />}
						/>
						<Route
							path="lead-insights"
							element={<LeadInsightsPage />}
						/>
						<Route
							path="lead-assignment-log"
							element={<LeadAssignmentAuditPage />}
						/>
						<Route path="referral-partners" element={<ReferralPartnerApprovalsPage />} />
						<Route path="crm-ipd-cases" element={<CrmIpdCasesPage />} />
						<Route path="crm-ipd-admitted" element={<CrmIpdAdmittedPage />} />
						<Route path="leads" element={<LeadsPage />} />
						<Route path="lab-reports" element={<LabReportsPage />} />
						<Route path="lab-payout" element={<LabPayoutReportPage />} />
						<Route path="expenses" element={<ExpensesPage />} />
						<Route path="additional-revenue" element={<AdditionalRevenuePage />} />
						<Route path="receivables" element={<ReceivablesPage />} />
						<Route path="accounting" element={<AccountingLayout />}>
							<Route index element={<AccountingDashboardPage />} />
							<Route path="vouchers" element={<AccountingVouchersPage />} />
							<Route path="vouchers/new" element={<AccountingVoucherFormPage />} />
							<Route path="vouchers/:voucherId" element={<AccountingVoucherDetailPage />} />
							<Route path="trial-balance" element={<AccountingTrialBalancePage />} />
							<Route path="general-ledger" element={<AccountingGeneralLedgerPage />} />
							<Route path="chart-of-accounts" element={<AccountingChartOfAccountsPage />} />
							<Route path="periods" element={<AccountingPeriodsPage />} />
							<Route path="opening-balances" element={<AccountingOpeningBalancesPage />} />
							<Route path="ops-reconciliation" element={<AccountingOpsReconciliationPage />} />
							<Route path="ageing" element={<AccountingAgeingPage />} />
							<Route path="tax" element={<AccountingTaxPage />} />
							<Route path="bank-pharmacy" element={<AccountingBankPharmacyPage />} />
							<Route path="statements" element={<AccountingStatementsPage />} />
							<Route path="cfo" element={<AccountingCfoPage />} />
						</Route>
						<Route path="marketing/meta-integration" element={<MetaIntegrationPage />} />
						<Route path="marketing/google-integration" element={<GoogleIntegrationPage />} />
						<Route path="meta-integration" element={<Navigate to="/super-admin/marketing/meta-integration" replace />} />
					</Route>

					{/* Hospital Admin Routes */}
					<Route
						path="/hospital-admin/*"
						element={
							<ProtectedRoute
								allowedRoles={[
									"HOSPITAL_ADMIN",
									"RECEPTIONIST",
									"SALES_PERSON",
									"SALES_MANAGER",
									"BUSINESS_ANALYST",
								]}
							>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/hospital-admin/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<HospitalDashboard />}
						/>
						<Route
							path="queue-management"
							element={<ReceptionistDashboard />}
						/>
						<Route path="chain/dashboard" element={<ChainDashboardPage />} />
						<Route path="chain/hospitals" element={<ChainHospitalsPage />} />
						<Route path="chain/admins" element={<ChainAdminsPage />} />
						<Route
							path="chain/referral-approvals"
							element={<Navigate to="/hospital-admin/partners/approvals" replace />}
						/>
						<Route path="partners/analytics" element={<PartnerAnalyticsPage />} />
						<Route path="partners/approvals" element={<PartnerApprovalsPage />} />
						<Route path="partners/leads" element={<PartnerLeadsPage />} />
						<Route path="partners/management" element={<PartnerManagementPage />} />
						<Route path="marketing/meta-integration" element={<MetaIntegrationPage />} />
						<Route path="marketing/google-integration" element={<GoogleIntegrationPage />} />
						<Route
							path="users"
							element={<UserManagement />}
						/>
						<Route
							path="mini-crm-settings"
							element={<MiniCrmSettings />}
						/>
						<Route
							path="integrations/sarvam"
							element={<SarvamIntegrationsPage />}
						/>
						<Route path="social/inbox" element={<SocialInboxPage />} />
						<Route path="social/settings" element={<SocialSettingsPage />} />
						<Route path="social/analytics" element={<SocialAnalyticsPage />} />
						<Route path="marketing/meta-dashboard" element={<MetaDashboardPage />} />
						<Route path="marketing/meta-event-logs" element={<MetaConversionLogsPage />} />
						<Route path="marketing/google-event-logs" element={<GoogleConversionLogsPage />} />
						<Route path="marketing/meta-attribution" element={<CampaignAttributionDashboardPage />} />
						<Route path="meta-conversion-logs" element={<Navigate to="/hospital-admin/marketing/meta-event-logs" replace />} />
						<Route path="campaign-attribution" element={<Navigate to="/hospital-admin/marketing/meta-attribution" replace />} />
						<Route
							path="referral-partners"
							element={<Navigate to="/hospital-admin/partners/management" replace />}
						/>
						<Route path="crm-ipd-cases" element={<CrmIpdCasesPage />} />
						<Route path="crm-ipd-admitted" element={<CrmIpdAdmittedPage />} />
						<Route path="leads" element={<LeadsPage />} />
						<Route
							path="lead-insights"
							element={<LeadInsightsPage />}
						/>
						<Route
							path="lead-assignment-log"
							element={<LeadAssignmentAuditPage />}
						/>
						<Route
							path="whatsapp-campaigns"
							element={<WhatsAppCampaignsPage />}
						/>
						<Route
							path="whatsapp-media-library"
							element={<WhatsAppMediaLibraryPage />}
						/>
						<Route path="lab-reports" element={<LabReportsPage />} />
						<Route path="lab-payout" element={<LabPayoutReportPage />} />
						<Route path="lab-external-vendors" element={<LabExternalVendorsPage />} />
						<Route
							path="pharmacy-vendors"
							element={<PharmacyVendorsPage basePath="/hospital-admin" adminMode />}
						/>
						<Route
							path="pharmacy-vendors/:vendorId"
							element={<PharmacyVendorDetailPage basePath="/hospital-admin" adminMode />}
						/>
						<Route path="ipd-lab-tests" element={<IPDLabTestManager adminMode />} />
						<Route path="pharmacy-bills-report" element={<PharmacySalesReportPage />} />
						<Route
							path="shifts"
							element={<ShiftManagement />}
						/>
						<Route
							path="opd-fees"
							element={<OpdFees />}
						/>
						<Route
							path="patients"
							element={<Patients />}
						/>
						<Route
							path="appointments"
							element={<AppointmentManagement />}
						/>
						<Route
							path="department"
							element={<DepartmentManagement />}
						/>
						<Route
							path="ipd-dashboard"
							element={<IPDAdminDashboard />}
						/>
						<Route
							path="ward-management"
							element={<WardManagement />}
						/>
						<Route
							path="insurance-management"
							element={<InsuranceManagement />}
						/>
						<Route path="loyalty" element={<LoyaltyProgramPage />} />
						<Route path="loyalty/app-analytics" element={<AppAnalyticsPage />} />
						<Route path="website-analytics" element={<WebsiteAnalyticsPage />} />
						<Route
							path="loyalty/truecare-welcome"
							element={<TrueCareWelcomeAnalyticsPage />}
						/>
						<Route
							path="intelligence"
							element={<IntelligenceCommandCentrePage />}
						/>
						<Route
							path="intelligence/doctors"
							element={<DoctorIntelligencePage />}
						/>
						<Route
							path="intelligence/departments"
							element={<DepartmentIntelligencePage />}
						/>
						<Route
							path="intelligence/operations"
							element={<OperationsIntelligencePage />}
						/>
						<Route
							path="intelligence/pharmacy"
							element={<PharmacyIntelligencePage />}
						/>
						<Route
							path="intelligence/lab"
							element={<LabIntelligencePage />}
						/>
						<Route
							path="intelligence/finance"
							element={<FinanceIntelligencePage />}
						/>
						<Route
							path="intelligence/attention"
							element={<ManagementAttentionPage />}
						/>
						<Route
							path="intelligence/benchmarks"
							element={<BenchmarkIntelligencePage />}
						/>
						<Route
							path="intelligence/settings"
							element={<IntelligenceSettingsPage />}
						/>
						<Route
							path="intelligence/data-quality"
							element={<IntelligenceDataQualityPage />}
						/>
						<Route
							path="intelligence/leakage"
							element={<LeakageIntelligencePage />}
						/>
						<Route
							path="intelligence/insights"
							element={<InsightsIntelligencePage />}
						/>
						<Route
							path="business-reports"
							element={<BusinessReports />}
						/>
						<Route
							path="pending-payments"
							element={<PendingPaymentsReportPage />}
						/>
						<Route
							path="patient-due-balances"
							element={<PatientDueBalancesPage />}
						/>
						<Route
							path="operational-reports"
							element={<OperationalReportsPage />}
						/>
						<Route
							path="customer-voice"
							element={<CustomerVoicePage />}
						/>
						<Route
							path="patient-wise-bills"
							element={<PatientWiseBillsReportPage />}
						/>
						<Route
							path="reconciliation"
							element={<ReconciliationPage />}
						/>
						<Route
							path="audit"
							element={<AuditLogPage />}
						/>
						<Route
							path="self-register-qr"
							element={<SelfRegisterQRPage />}
						/>
						<Route
							path="gst-management"
							element={<GSTManagement />}
						/>
						<Route
							path="daycare-setup"
							element={<DayCareSetupPage />}
						/>
						<Route path="ipd-queue" element={<IPDQueuePage />} />
						<Route path="ipd-offline-queue" element={<IPDOfflineQueuePage />} />
						<Route path="ipd-pending-collection" element={<IPDPendingCollectionPage />} />
						<Route
							path="ipd-bill-edit-requests"
							element={<IPDBillEditRequestsPage />}
						/>
						<Route
							path="ipd-excess-collection"
							element={<IpdExcessCollectionPage />}
						/>
						<Route path="ipd/:admissionId/billing" element={<IPDBillingPage />} />
						<Route path="ipd/:admissionId/offline-billing" element={<IPDBillingPage />} />
						<Route path="ipd/:admissionId" element={<IPDPatientDetail />} />
						<Route
							path="ipd/:admissionId/surgeries/create"
							element={<IPDPatientDetail />}
						/>
						<Route path="daycare" element={<DayCareQueuePage />} />
						<Route path="daycare/:caseId" element={<DayCareCasePage />} />
						<Route path="daycare/:caseId/billing" element={<DayCareBillingPage />} />
						<Route path="expenses" element={<ExpensesPage />} />
						<Route path="additional-revenue" element={<AdditionalRevenuePage />} />
						<Route path="receivables" element={<ReceivablesPage />} />
						<Route path="vendor-payments" element={<VendorManagementPage />} />
						<Route path="accounting" element={<AccountingLayout />}>
							<Route index element={<AccountingDashboardPage />} />
							<Route path="vouchers" element={<AccountingVouchersPage />} />
							<Route path="vouchers/new" element={<AccountingVoucherFormPage />} />
							<Route path="vouchers/:voucherId" element={<AccountingVoucherDetailPage />} />
							<Route path="trial-balance" element={<AccountingTrialBalancePage />} />
							<Route path="general-ledger" element={<AccountingGeneralLedgerPage />} />
							<Route path="chart-of-accounts" element={<AccountingChartOfAccountsPage />} />
							<Route path="periods" element={<AccountingPeriodsPage />} />
							<Route path="opening-balances" element={<AccountingOpeningBalancesPage />} />
							<Route path="ops-reconciliation" element={<AccountingOpsReconciliationPage />} />
							<Route path="ageing" element={<AccountingAgeingPage />} />
							<Route path="tax" element={<AccountingTaxPage />} />
							<Route path="bank-pharmacy" element={<AccountingBankPharmacyPage />} />
							<Route path="statements" element={<AccountingStatementsPage />} />
							<Route path="cfo" element={<AccountingCfoPage />} />
						</Route>
					</Route>

					{/* Patient Details View Route (Standalone) */}
					<Route
						path="/patient/:patientId"
						element={
							<ProtectedRoute
								allowedRoles={[
									"SUPER_ADMIN",
									"HOSPITAL_ADMIN",
									"RECEPTIONIST",
									"SALES_PERSON",
									"DOCTOR",
									"RMO"
								]}
							>
								<PatientDetailsWrapper />
							</ProtectedRoute>
						}
					/>

					{/* Receptionist / Operations Manager Routes */}
					<Route
						path="/receptionist/*"
						element={
							<ProtectedRoute allowedRoles={["RECEPTIONIST", "OPERATIONS_MANAGER"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/receptionist/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<ReceptionistDashboard />}
						/>
						<Route
							path="queue-management"
							element={<ReceptionistDashboard />}
						/>
						<Route
							path="patients"
							element={<Patients />}
						/>
						<Route
							path="patient-bills"
							element={<PatientBillsPage />}
						/>
						<Route
							path="patient-due-balances"
							element={<PatientDueBalancesPage />}
						/>
						<Route
							path="appointments"
							element={<AppointmentManagement />}
						/>
						<Route
							path="daycare"
							element={<DayCareQueuePage />}
						/>
						<Route
							path="daycare/:caseId"
							element={<DayCareCasePage />}
						/>
						<Route
							path="daycare/:caseId/billing"
							element={<DayCareBillingPage />}
						/>
						<Route
							path="follow-ups"
							element={<FollowUpsSection />}
						/>
						<Route
							path="ipd-discharge-follow-ups"
							element={<IpdDischargeFollowUpsPage />}
						/>
						<Route
							path="surgical-appointments"
							element={<SurgicalAppointments />}
						/>
						<Route
							path="self-register-qr"
							element={<SelfRegisterQRPage />}
						/>
						<Route
							path="create-test"
							element={<CreateLabTestAppointment />}
						/>
						<Route
							path="external-tests"
							element={<Navigate to="/receptionist/create-test" replace />}
						/>
						<Route
							path="pending-lab-bills"
							element={<PendingLabBills />}
						/>
						<Route
							path="reconciliation"
							element={<ReconciliationPage />}
						/>
						<Route
							path="ipd-queue"
							element={<IPDQueuePage />}
						/>
						<Route
							path="ipd-offline-queue"
							element={<IPDOfflineQueuePage />}
						/>
						<Route
							path="ipd-pending-collection"
							element={<IPDPendingCollectionPage />}
						/>
						<Route
							path="ipd/:admissionId/billing"
							element={<IPDBillingPage />}
						/>
						<Route
							path="ipd/:admissionId/offline-billing"
							element={<IPDBillingPage />}
						/>
						<Route
							path="ipd/:admissionId"
							element={<IPDPatientDetail />}
						/>
						<Route
							path="ipd/:admissionId/surgeries/create"
							element={<IPDPatientDetail />}
						/>
						<Route path="expenses" element={<ExpensesPage />} />
						<Route path="vendor-payments" element={<VendorManagementPage />} />
						<Route path="customer-voice" element={<CustomerVoicePage />} />
						<Route
							path="business-reports"
							element={
								<ProtectedRoute allowedRoles={["OPERATIONS_MANAGER"]}>
									<BusinessReports summaryOnly />
								</ProtectedRoute>
							}
						/>
					</Route>

					{/* Sales Person Routes */}
					<Route
						path="/sales-person/*"
						element={
							<ProtectedRoute allowedRoles={["SALES_PERSON", "SALES_MANAGER", "EXTERNAL_PARTNER"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/sales-person/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<SalesDashboard />}
						/>
						<Route
							path="queue-management"
							element={<SalesManagerQueueManagementPage />}
						/>
						<Route
							path="leads"
							element={<LeadsPage />}
						/>
						<Route
							path="sales-report"
							element={
								<ProtectedRoute allowedRoles={["SALES_MANAGER"]}>
									<BusinessReports salesOnly />
								</ProtectedRoute>
							}
						/>
						<Route path="crm-ipd-cases" element={<CrmIpdCasesPage />} />
						<Route path="crm-ipd-admitted" element={<CrmIpdAdmittedPage />} />
						<Route
							path="patients"
							element={<Patients />}
						/>
						<Route
							path="appointments"
							element={<AppointmentManagement />}
						/>
						<Route
							path="follow-ups"
							element={<FollowUpsSection />}
						/>
						<Route
							path="surgical-appointments"
							element={<SurgicalAppointments />}
						/>
						<Route
							path="lead-insights"
							element={<LeadInsightsPage />}
						/>
						<Route
							path="lead-assignment-log"
							element={<LeadAssignmentAuditPage />}
						/>
						<Route path="ai-outcomes" element={<SarvamAgentOutcomesPage />} />
						<Route
							path="sarvam-outcomes"
							element={<Navigate to="/sales-person/ai-outcomes" replace />}
						/>
					</Route>

					{/* Doctor team lead portal */}
					<Route
						path="/doctor-team/*"
						element={
							<ProtectedRoute allowedRoles={["DOCTOR_TEAM_LEAD"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route index element={<Navigate to="/doctor-team/dashboard" replace />} />
						<Route path="dashboard" element={<DoctorTeamDashboard />} />
					</Route>

					{/* Doctor Routes (includes team lead clinical OPD) */}
					<Route
						path="/doctor/*"
						element={
							<ProtectedRoute allowedRoles={DOCTOR_PORTAL_ROLE_LIST}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/doctor/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<DoctorDashboard />}
						/>
						<Route
							path="appointments"
							element={<Appointments />}
						/>
						<Route
							path="daycare"
							element={<DayCareQueuePage />}
						/>
						<Route
							path="ipd-management"
							element={<IPDManagement />}
						/>
						<Route
							path="ipd/:admissionId"
							element={<IPDPatientDetail />}
						/>
						<Route
							path="ipd/:admissionId/surgeries/create"
							element={<IPDPatientDetail />}
						/>
						<Route
							path="template"
							element={<DiseaseTemplate />}
						/>
						<Route
							path="create"
							element={<CreateLabTest />}
						/>
					</Route>

					{/* Nurse Routes */}
					<Route
						path="/nurse/*"
						element={
							<ProtectedRoute allowedRoles={["NURSE"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/nurse/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<NurseDashboard />}
						/>
						<Route
							path="ipd-management"
							element={<NurseIPDManagement />}
						/>
					</Route>

					<Route
						path="doctor/consultation/:patientId/:appointmentId"
						element={
							<ProtectedRoute allowedRoles={DOCTOR_PORTAL_ROLE_LIST}>
								<ConsultationPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="doctor/diagnosis-record/:appointmentId"
						element={
							<ProtectedRoute allowedRoles={DOCTOR_PORTAL_ROLE_LIST}>
								<DiagnosisRecord />
							</ProtectedRoute>
						}
					/>

					{/* Pharmacist Routes */}
					<Route
						path="/pharmacist/*"
						element={
							<ProtectedRoute allowedRoles={["PHARMACIST"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/pharmacist/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<PharmacistDashboard />}
						/>
						<Route
							path="queue"
							element={<PharmacyQueuePage />}
						/>
						<Route path="ipd-pharma" element={<PharmacistIPDPharmaPage />} />
						<Route
							path="stock"
							element={<PharmacyStockPage />}
						/>
					<Route
						path="stock-in"
						element={<PharmacyStockInPage />}
					/>
					<Route
						path="purchase-orders"
						element={<PharmacyPurchaseOrdersPage />}
					/>
					<Route
						path="vendors"
						element={<VendorsPage />}
					/>
						<Route
							path="vendors/:vendorId"
							element={<VendorDetailPage />}
						/>
						<Route
							path="ot"
							element={<OTPharmacyDashboardPage />}
						/>
						<Route
							path="ot/transfer"
							element={<OTTransferPage />}
						/>
						<Route
							path="ot/consumption"
							element={<OTConsumptionEntryPage />}
						/>
						<Route
							path="ot/general"
							element={<OTGeneralConsumptionPage />}
						/>
						<Route
							path="ot/reconcile"
							element={<OTReconciliationPage />}
						/>
						<Route
							path="ot/reports"
							element={<OTReportsPage />}
						/>
						<Route
							path="walk-in-sale"
							element={<PharmacyWalkInSalePage />}
						/>
						<Route
							path="sales-report"
							element={<PharmacySalesReportPage />}
						/>
						<Route
							path="returns"
							element={<PharmacyReturnsPage />}
						/>
						<Route
							path="return-history"
							element={<PharmacyReturnHistoryPage />}
						/>
					</Route>

					{/* Lab Technician Routes */}
					<Route
						path="/lab/*"
						element={
							<ProtectedRoute allowedRoles={["LAB_TECHNICIAN"]}>
								<UnifiedLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Navigate
									to="/lab/dashboard"
									replace
								/>
							}
						/>
						<Route
							path="dashboard"
							element={<LabDashboard />}
						/>
						<Route
							path="from-doctors"
							element={<TestFromDoctors />}
						/>
						<Route
							path="from-receptionist"
							element={<TestsFromReceptionist />}
						/>
						<Route
							path="ipd-tests"
							element={<IPDLabTestManager />}
						/>
						<Route
							path="completed-tests"
							element={<CompletedTests />}
						/>
						<Route
							path="create"
							element={<CreateLabTest />}
						/>
						<Route path="reports" element={<LabReportsPage />} />
					</Route>

				</Routes>
			</SearchProvider>
		</AuthProvider>
	);
}
